import React, { useCallback, useState, type FC } from 'react';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Drawer from '@atlaskit/drawer';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { NavigationPrompt } from '@atlassian/help-center-common-component/prompt';
import { useActions, useConversationHasMessages, useError } from '../controllers/conversation-store';
import type { ui_virtualAgentConversationQuery } from './__generated__/ui_virtualAgentConversationQuery.graphql';
import { ClearConversation } from './clear-conversation';
import { Conversation } from './conversation';
import { ErrorPage } from './error-page';
import { LazyLeaveConversationModal } from './leave-conversation-modal/async';

export const VirtualAgentConversationPage: FC = () => {
    di(useError, useConversationHasMessages, Conversation, NavigationPrompt, ClearConversation);

    const hasMessages = useConversationHasMessages();
    const error = useError();

    const data = useLazyLoadQuery<ui_virtualAgentConversationQuery>(
        graphql`
            query ui_virtualAgentConversationQuery {
                me {
                    user {
                        ...conversationHelpseeker
                    }
                }
            }
        `,
        {}
    );

    if (error) {
        return <ErrorPage />;
    }

    return (
        <>
            <Box xcss={containerStyles}>
                {hasMessages && (
                    <Inline space="space.300" alignInline="end" xcss={clearConversationContainer}>
                        <ClearConversation />
                    </Inline>
                )}
                <Inline xcss={conversationStyles} alignInline="center">
                    <Conversation userRef={data.me.user} />
                </Inline>
            </Box>
            <NavigationPrompt preventNavigation={hasMessages} />
        </>
    );
};

export const VirtualAgentConversationDrawer: FC<{
    onClose: () => void;
}> = ({ onClose }) => {
    di(useActions, useError, useConversationHasMessages, NavigationPrompt, ClearConversation);

    const hasMessages = useConversationHasMessages();
    const { clearConversation } = useActions();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [showLeaveConversationModal, setShowLeaveConversationModal] = useState(false);
    const openModal = useCallback(() => setShowLeaveConversationModal(true), []);
    const closeModal = useCallback(() => setShowLeaveConversationModal(false), []);

    const leaveConversation = useCallback(() => {
        clearConversation();
        createAnalyticsEvent({
            analyticsType: TRACK_EVENT_TYPE,
            action: 'left',
            actionSubject: 'virtualAgentConversation',
        }).fire();
    }, [clearConversation, createAnalyticsEvent]);

    const handleLeaveButtonClick = useCallback(() => {
        leaveConversation();
        closeModal();
        onClose();
    }, [leaveConversation, onClose, closeModal]);

    const handleDrawerCloseButtonClick = useCallback(() => {
        if (hasMessages) {
            openModal();
        } else {
            onClose();
        }
    }, [hasMessages, onClose, openModal]);

    return (
        <Drawer zIndex={504} isOpen onClose={handleDrawerCloseButtonClick} width="full" testId="virtual-agent-drawer">
            <VirtualAgentConversationPage />
            <ModalTransition>
                {showLeaveConversationModal && (
                    <LazyLeaveConversationModal onClose={closeModal} onLeaveButtonClick={handleLeaveButtonClick} />
                )}
            </ModalTransition>
        </Drawer>
    );
};

const containerStyles = xcss({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const conversationStyles = xcss({
    height: '100%',
    flexGrow: 1,
    minHeight: token('space.800', '64px'),
});

const clearConversationContainer = xcss({
    marginRight: token('space.300', '24px'),
});
